import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../contexts/PermissionsContext';
import { getSpaAppointmentCommunications } from '../APIs/SpaAPI';
import { COMMUNICATION_STATUSES } from '../utils/spaCommunications';
import useSpaCommunicationCapabilities from '../hooks/useSpaCommunicationCapabilities';
import SpaCommunicationDeliveries from './SpaCommunicationDeliveries';
import SpaCommunicationSendDialog from './SpaCommunicationSendDialog';

export default function SpaAppointmentCommunications({ appointmentId, token, tenant }) {
  const { t } = useTranslation();
  const { can, loading: permissionsLoading } = usePermissions();
  const allowed = !permissionsLoading && (can('spa-appointments:view') || can('spa-communications:view'));
  const viewCommunications = !permissionsLoading && can('spa-communications:view');
  const canSend = allowed && viewCommunications && can('spa-communications:send');
  const capabilities = useSpaCommunicationCapabilities(token, tenant, viewCommunications && Boolean(appointmentId));
  const [result, setResult] = useState(null);
  const [reload, setReload] = useState(0);
  const [filter, setFilter] = useState('ALL');
  const [sending, setSending] = useState(false);
  const key = `${tenant}:${token}:${appointmentId}:${allowed}`;
  const current = result?.key === key ? result : null;
  const ct = (name) => t(`spa.communications.${name}`);

  useEffect(() => {
    if (!allowed || !token || !tenant || !appointmentId) return undefined;
    const controller = new AbortController();
    setResult({ key, loading: true });
    const load = async () => {
      try {
        const data = await getSpaAppointmentCommunications(appointmentId, token, tenant, { signal: controller.signal });
        if (!Array.isArray(data) || data.some((item) => !item || item.id == null || typeof item.status !== 'string')) {
          throw new Error(t('spa.communications.invalidResponse'));
        }
        if (!controller.signal.aborted) setResult({ key, data });
      } catch (error) {
        if (!controller.signal.aborted) setResult({ key, error: error.message || t('spa.communications.historyError') });
      }
    };
    load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, token, tenant, appointmentId, key, reload]);

  if (!allowed || !token || !tenant || !appointmentId) return null;
  const refresh = () => setReload((value) => value + 1);
  const rows = current?.data?.filter((item) => filter === 'ALL' || item.status === filter) || [];
  return <Box component="section" sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography component="h3" variant="h6">{ct('historyTitle')}</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={ct('refresh')}><IconButton aria-label={ct('refresh')} disabled={current?.loading} onClick={refresh}><RefreshIcon /></IconButton></Tooltip>
          {canSend && <Button startIcon={<SendIcon />} disabled={!capabilities.data} onClick={() => setSending(true)}>{ct('newCommunication')}</Button>}
        </Stack>
      </Stack>
      <TextField select size="small" label={ct('deliveryStatus')} value={filter} onChange={(event) => setFilter(event.target.value)}>
        <MenuItem value="ALL">{ct('allStatuses')}</MenuItem>
        {COMMUNICATION_STATUSES.map((status) => <MenuItem key={status} value={status}>{ct(`statuses.${status}`)}</MenuItem>)}
      </TextField>
      {(!current || current.loading) && <CircularProgress size={20} aria-label={ct('historyLoading')} />}
      {current?.error && <Alert severity="error">{current.error}</Alert>}
      {current?.data && !rows.length && <Typography color="text.secondary">{ct('historyEmpty')}</Typography>}
      {current?.data && <SpaCommunicationDeliveries items={rows} token={token} tenant={tenant} canSend={canSend} capabilities={capabilities.data} onChange={refresh} />}
      {capabilities.error && canSend && <Alert severity="warning">{ct('readinessError')} {capabilities.error}</Alert>}
      {sending && canSend && <SpaCommunicationSendDialog open token={token} tenant={tenant} capabilities={capabilities.data}
        target={{ type: 'appointmentId', id: Number(appointmentId) }} onClose={() => setSending(false)} onResult={refresh} />}
    </Stack>
  </Box>;
}
